/* eslint-disable max-lines -- Keep the ordered Realtime lifecycle in one hook; pure helpers live separately. */
import { useCallback, useEffect, useRef } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"

import type {
  RealtimeAssistantCompletion,
  RealtimeCompletedTool,
  RealtimeInterruptEvent,
  RealtimeTranscriptionFailure,
} from "./use-realtime-audio"
import type { CompletedVoiceTool } from "./voice-mode-handler-utils"
import type { ActivityMessage, ChatAppResponse } from "@/api/models"

import { routeFairUseError } from "@/features/fair-use/fair-use-ws-bridge"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"

import { createVoiceTranscriptResponse } from "./use-chat-history-helpers"
import {
  assistantFilesFromToolCalls,
  createStreamingVoiceResponse,
  createToolActivity,
  mergeCompletedVoiceTools,
  mergeVoiceActivities,
} from "./voice-mode-handler-utils"

type UseVoiceModeHandlersOptions = {
  currentSessionId: string | null | undefined
  onVoiceMessage?: (
    userText: string,
    response: ChatAppResponse,
    backendSessionId?: string
  ) => void | Promise<void>
  /** Callback to set streaming answer for real-time text display */
  onStreamingVoiceMessage?: (userText: string, response: ChatAppResponse) => void
  /** Callback to clear streaming answer when complete */
  onStreamingComplete?: () => void
  /** Route used when voice mode creates a conversation before the page has one. */
  voiceSessionRoutePrefix?: string
}

type PendingVoiceAssistantResponse = {
  text: string
  activities: ActivityMessage[]
  toolCalls: CompletedVoiceTool[]
  assistantFiles: string[]
  inputItemId?: string
  /** GPT-Live speech that answers no caller turn; never paired with a later one. */
  unprompted?: boolean
}

type VoiceToolLifecycle = Pick<
  PendingVoiceAssistantResponse,
  "activities" | "toolCalls" | "assistantFiles"
>

function hasVoiceToolLifecycle(lifecycle: VoiceToolLifecycle): boolean {
  return Boolean(
    lifecycle.activities.length || lifecycle.toolCalls.length || lifecycle.assistantFiles.length
  )
}

/** Earlier lifecycle first: tools that ran before a handoff precede the continuation's. */
function mergeVoiceToolLifecycles(
  earlier: VoiceToolLifecycle,
  later: VoiceToolLifecycle
): VoiceToolLifecycle {
  return {
    activities: [...earlier.activities, ...later.activities],
    toolCalls: mergeCompletedVoiceTools(earlier.toolCalls, later.toolCalls),
    assistantFiles: Array.from(new Set([...earlier.assistantFiles, ...later.assistantFiles])),
  }
}

function isMismatchedVoiceItem(incomingItemId?: string, boundItemId?: string): boolean {
  return Boolean(incomingItemId && boundItemId && incomingItemId !== boundItemId)
}

function completedToolsFromRealtime(toolCalls: RealtimeCompletedTool[]): CompletedVoiceTool[] {
  return toolCalls.map((toolCall) => ({
    name: toolCall.name,
    ...(toolCall.callId ? { callId: toolCall.callId } : {}),
    success: toolCall.success,
    ...(toolCall.assistantFiles.length ? { assistantFiles: toolCall.assistantFiles } : {}),
  }))
}

function mergeRealtimeCompletionIntoSnapshot(
  snapshot: PendingVoiceAssistantResponse,
  text: string,
  completion?: RealtimeAssistantCompletion
): void {
  if (text) {
    snapshot.text = text
  }
  if (!completion) return
  snapshot.toolCalls = mergeCompletedVoiceTools(
    snapshot.toolCalls,
    completedToolsFromRealtime(completion.toolCalls)
  )
  snapshot.activities = mergeVoiceActivities(snapshot.activities, completion)
  snapshot.assistantFiles = Array.from(
    new Set([
      ...snapshot.assistantFiles,
      ...completion.assistantFiles,
      ...assistantFilesFromToolCalls(snapshot.toolCalls),
    ])
  )
}

/**
 * Hook to manage voice mode state and handlers.
 * Handles session creation, navigation after voice mode ends, and cache invalidation.
 */
export function useVoiceModeHandlers({
  currentSessionId,
  onVoiceMessage,
  onStreamingVoiceMessage,
  onStreamingComplete,
  voiceSessionRoutePrefix = "/explorer/chat",
}: UseVoiceModeHandlersOptions) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const onVoiceMessageRef = useRef(onVoiceMessage)
  onVoiceMessageRef.current = onVoiceMessage
  const voicePersistenceTailRef = useRef<Promise<void> | null>(null)

  // Track current user transcript for voice mode
  const voiceUserTranscriptRef = useRef<string>("")
  const voiceUserItemIdRef = useRef<string | undefined>(undefined)

  // Track accumulated assistant text for streaming
  const accumulatedAssistantTextRef = useRef<string>("")
  const activeAssistantInputItemIdRef = useRef<string | undefined>(undefined)

  // Track activities during the current voice response
  const currentActivitiesRef = useRef<ActivityMessage[]>([])

  // Track completed tool calls for the current exchange
  const completedToolCallsRef = useRef<CompletedVoiceTool[]>([])
  const currentAssistantFilesRef = useRef<string[]>([])

  // Track if we're currently streaming an assistant response
  const isStreamingRef = useRef<boolean>(false)

  // Hold a completed assistant response whose user transcript hasn't arrived yet.
  // The Realtime API's Whisper transcription (input_audio_transcription.completed)
  // is asynchronous and, for short utterances, can land AFTER
  // response.audio_transcript.done. WebSocket messages and their callbacks are
  // dispatched synchronously in receive order, so this local FIFO pairs ordered
  // late transcripts without adding a protocol identity.
  const pendingAssistantResponsesRef = useRef<PendingVoiceAssistantResponse[]>([])

  // Tool lifecycle (activities, tool calls, files) of a pending snapshot whose
  // provider output was discarded, keyed by input item. It is deliberately kept
  // out of the pairing FIFO: discarded output must never become the answer a
  // late user transcript pairs with. The item's user turn or real answer
  // inherits it instead.
  const discardedToolLifecyclesRef = useRef<Map<string, VoiceToolLifecycle>>(new Map())

  const takeDiscardedToolLifecycle = useCallback(
    (inputItemId?: string): VoiceToolLifecycle | undefined => {
      if (!inputItemId) return undefined
      const lifecycle = discardedToolLifecyclesRef.current.get(inputItemId)
      discardedToolLifecyclesRef.current.delete(inputItemId)
      return lifecycle
    },
    []
  )

  // A failed input transcript is an item-scoped ASR failure, not a failed
  // Realtime turn. Remember that exact item until its assistant response has
  // completed so it can be omitted without shifting the next transcript.
  const failedTranscriptionItemIdsRef = useRef<Set<string>>(new Set())
  const anonymousFailedTranscriptionsRef = useRef(0)

  // Track session created during voice mode - we'll navigate after voice mode ends
  const voiceSessionIdRef = useRef<string | null>(null)

  // Whether this voice-mode run committed any message. The backend soft-deletes
  // a background-created session that ended with no content, so navigating to
  // it after an empty run would open an inactive session.
  const committedInCurrentRunRef = useRef(false)

  // An explicit page session is authoritative for a newly started voice
  // lifecycle. This prevents a backend-created session from an earlier voice
  // run leaking into attachments created later in another chat.
  useEffect(() => {
    voiceSessionIdRef.current = currentSessionId ?? null
  }, [currentSessionId])

  const persistVoiceMessage = useCallback((userText: string, response: ChatAppResponse) => {
    // Every commit path (paired exchange, barge-in, hang-up flush) goes through
    // here, so this is the single record that the current run produced content.
    committedInCurrentRunRef.current = true
    const invokeLatest = () =>
      onVoiceMessageRef.current?.(userText, response, voiceSessionIdRef.current ?? undefined)
    const track = (operation: Promise<void>) => {
      voicePersistenceTailRef.current = operation
      void operation.then(
        () => {
          if (voicePersistenceTailRef.current === operation) {
            voicePersistenceTailRef.current = null
          }
        },
        (error: unknown) => {
          logger.error(
            "Failed to persist voice exchange",
            error instanceof Error ? error : { error }
          )
          if (voicePersistenceTailRef.current === operation) {
            voicePersistenceTailRef.current = null
          }
        }
      )
    }

    const currentTail = voicePersistenceTailRef.current
    if (!currentTail) {
      // Keep existing synchronous callbacks synchronous. Only async callers
      // establish a tail that later exchanges must await.
      const result = invokeLatest()
      if (result && typeof result.then === "function") {
        track(Promise.resolve(result))
      }
      return
    }

    // Both real page callbacks may create a session before appending. Run the
    // next exchange only after the previous callback settles, and resolve the
    // callback ref at execution time so it sees the latest page/store closure.
    const operation = currentTail.then(invokeLatest, invokeLatest).then(() => undefined)
    track(operation)
  }, [])

  /**
   * Commit a finished exchange to chat history + persistence, then reset all
   * per-exchange tracking state for the next turn.
   */
  const commitVoiceExchange = useCallback(
    (
      {
        userText,
        assistantText,
        activities,
        toolCalls,
        assistantFiles,
      }: {
        userText: string
        assistantText: string
        activities: ActivityMessage[]
        toolCalls: CompletedVoiceTool[]
        assistantFiles: string[]
      },
      { preserveActiveState = false }: { preserveActiveState?: boolean } = {}
    ) => {
      if (!preserveActiveState) {
        // Clear streaming UI state
        onStreamingComplete?.()
      }

      // Add the final message with activities and tool calls
      const response = createVoiceTranscriptResponse(assistantText, {
        activities,
        toolCalls,
        assistantFiles,
        sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
      })
      persistVoiceMessage(userText, response)

      if (preserveActiveState) return

      // Reset for next exchange
      voiceUserTranscriptRef.current = ""
      voiceUserItemIdRef.current = undefined
      accumulatedAssistantTextRef.current = ""
      activeAssistantInputItemIdRef.current = undefined
      currentActivitiesRef.current = []
      completedToolCallsRef.current = []
      currentAssistantFilesRef.current = []
      isStreamingRef.current = false
    },
    [currentSessionId, onStreamingComplete, persistVoiceMessage]
  )

  const handleVoiceUserTranscript = useCallback(
    (text: string, itemId?: string) => {
      // The assistant already finished for this turn but its transcript arrived
      // late (short-utterance Whisper lag): pair them and commit now, rather than
      // resetting the accumulated answer and discarding it.
      // Unprompted GPT-Live speech (a greeting) waits in the same FIFO so it is
      // not lost, but it answers nobody: it goes on the record as its own
      // turn, in order, before this caller turn — never as this turn's answer.
      const held = pendingAssistantResponsesRef.current
      const unprompted = held.filter((response) => response.unprompted)
      if (unprompted.length > 0) {
        pendingAssistantResponsesRef.current = held.filter((response) => !response.unprompted)
        for (const spoken of unprompted) {
          commitVoiceExchange(
            {
              userText: "",
              assistantText: spoken.text,
              activities: spoken.activities,
              toolCalls: spoken.toolCalls,
              assistantFiles: spoken.assistantFiles,
            },
            { preserveActiveState: true }
          )
        }
      }

      const pendingResponses = pendingAssistantResponsesRef.current
      let pendingIndex = -1
      if (itemId) {
        pendingIndex = pendingResponses.findIndex((response) => response.inputItemId === itemId)
      } else if (pendingResponses.length > 0) {
        pendingIndex = 0
      }
      if (
        pendingIndex < 0 &&
        itemId &&
        pendingResponses.length > 0 &&
        pendingResponses.every((response) => !response.inputItemId)
      ) {
        pendingIndex = 0
      }
      const pending = pendingIndex >= 0 ? pendingResponses.splice(pendingIndex, 1)[0] : undefined
      // Tools that already ran for this item before its native output was
      // discarded belong to this user turn, never to a blank assistant entry.
      const discardedLifecycle = takeDiscardedToolLifecycle(itemId)
      if (pending) {
        const lifecycle = discardedLifecycle
          ? mergeVoiceToolLifecycles(discardedLifecycle, pending)
          : pending
        commitVoiceExchange(
          {
            userText: text,
            assistantText: pending.text,
            activities: lifecycle.activities,
            toolCalls: lifecycle.toolCalls,
            assistantFiles: lifecycle.assistantFiles,
          },
          { preserveActiveState: true }
        )
        return
      }

      // The assistant is mid-stream and the transcript arrived late: attach it to
      // the in-flight response instead of wiping the already-streamed text.
      // A provider input item owns its user text, tools, files, and completion.
      // Rebind only when the incoming id is missing, the in-flight turn has no
      // user item id yet, or the ids match (late Whisper for this item).
      const hasInFlightResponse = Boolean(
        isStreamingRef.current ||
        accumulatedAssistantTextRef.current ||
        currentActivitiesRef.current.length ||
        completedToolCallsRef.current.length ||
        currentAssistantFilesRef.current.length
      )
      if (hasInFlightResponse) {
        const boundItemId = voiceUserItemIdRef.current
        const isDifferentTurn = Boolean(itemId && boundItemId && itemId !== boundItemId)
        if (!isDifferentTurn) {
          if (discardedLifecycle) {
            const lifecycle = mergeVoiceToolLifecycles(discardedLifecycle, {
              activities: currentActivitiesRef.current,
              toolCalls: completedToolCallsRef.current,
              assistantFiles: currentAssistantFilesRef.current,
            })
            currentActivitiesRef.current = lifecycle.activities
            completedToolCallsRef.current = lifecycle.toolCalls
            currentAssistantFilesRef.current = lifecycle.assistantFiles
          }
          voiceUserTranscriptRef.current = text
          voiceUserItemIdRef.current = itemId
          onStreamingVoiceMessage?.(
            text,
            createStreamingVoiceResponse({
              text: accumulatedAssistantTextRef.current,
              activities: currentActivitiesRef.current,
              assistantFiles: currentAssistantFilesRef.current,
              sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
            })
          )
          return
        }

        // Later utterance with a different item id: commit the bound exchange
        // first (same idea as barge-in when pendingUserText is already set),
        // then fall through to the new-turn path.
        commitVoiceExchange({
          userText: voiceUserTranscriptRef.current,
          assistantText: accumulatedAssistantTextRef.current,
          activities: currentActivitiesRef.current,
          toolCalls: completedToolCallsRef.current,
          assistantFiles: currentAssistantFilesRef.current,
        })
      }

      // Normal path: a new user turn is starting.
      voiceUserTranscriptRef.current = text
      voiceUserItemIdRef.current = itemId
      // Reset streaming state for new user input
      accumulatedAssistantTextRef.current = ""
      currentActivitiesRef.current = discardedLifecycle ? [...discardedLifecycle.activities] : []
      completedToolCallsRef.current = discardedLifecycle ? [...discardedLifecycle.toolCalls] : []
      currentAssistantFilesRef.current = discardedLifecycle
        ? [...discardedLifecycle.assistantFiles]
        : []
      isStreamingRef.current = false
      if (discardedLifecycle && onStreamingVoiceMessage) {
        // Keep the tool cards/files that already ran visible on this user turn
        // while its verified continuation is produced.
        onStreamingVoiceMessage(
          text,
          createStreamingVoiceResponse({
            text: "",
            activities: currentActivitiesRef.current,
            assistantFiles: currentAssistantFilesRef.current,
            sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
          })
        )
      }
    },
    [commitVoiceExchange, currentSessionId, onStreamingVoiceMessage, takeDiscardedToolLifecycle]
  )

  const handleVoiceTranscriptionFailure = useCallback((failure: RealtimeTranscriptionFailure) => {
    const pendingResponses = pendingAssistantResponsesRef.current
    let pendingIndex = -1
    if (failure.itemId) {
      pendingIndex = pendingResponses.findIndex(
        (response) => response.inputItemId === failure.itemId
      )
    } else if (pendingResponses.length > 0) {
      pendingIndex = 0
    }
    if (
      pendingIndex < 0 &&
      failure.itemId &&
      pendingResponses.length > 0 &&
      pendingResponses.every((response) => !response.inputItemId)
    ) {
      pendingIndex = 0
    }

    // An unavailable transcript drops the whole turn, including tool lifecycle
    // retained from its discarded native output.
    const hadDiscardedLifecycle = discardedToolLifecyclesRef.current.delete(failure.itemId ?? "")

    if (pendingIndex >= 0 || hadDiscardedLifecycle) {
      if (pendingIndex >= 0) pendingResponses.splice(pendingIndex, 1)
      logger.info("Dropped completed voice response with unavailable input transcript", {
        code: failure.code ?? "unknown",
      })
      return
    }

    if (failure.itemId) {
      failedTranscriptionItemIdsRef.current.add(failure.itemId)
      if (!activeAssistantInputItemIdRef.current) {
        activeAssistantInputItemIdRef.current = failure.itemId
      }
    } else {
      anonymousFailedTranscriptionsRef.current += 1
    }
  }, [])

  const consumeFailedTranscription = useCallback((inputItemId?: string) => {
    if (inputItemId && failedTranscriptionItemIdsRef.current.delete(inputItemId)) {
      return true
    }
    if (anonymousFailedTranscriptionsRef.current > 0) {
      anonymousFailedTranscriptionsRef.current -= 1
      return true
    }
    return false
  }, [])

  /**
   * Handle user interrupting the AI (starts speaking while AI is talking).
   * Clears the streaming state so the UI resets for the new exchange.
   */
  const handleVoiceInterrupt = useCallback(
    (event?: RealtimeInterruptEvent) => {
      if (event?.reason === "response_output_discarded") {
        // A tool-only completion or a superseded native cancel is a handoff,
        // not a failed user turn. Drop only model-authored assistant text;
        // keep the user, tools, files, and input identity for the continuation.
        const discardedInputItemId = event.inputItemId
        if (discardedInputItemId) {
          // A completed answer for this input may already wait in the pairing
          // FIFO. Discarded output must never be committed by — or even be a
          // pairing target for — its late user transcript. Remove it from the
          // FIFO; only its tool lifecycle moves to the item-keyed stash that the
          // item's user turn or real answer inherits.
          const retainedPending: PendingVoiceAssistantResponse[] = []
          for (const response of pendingAssistantResponsesRef.current) {
            if (response.inputItemId !== discardedInputItemId) {
              retainedPending.push(response)
              continue
            }
            if (!hasVoiceToolLifecycle(response)) continue
            const stashed = discardedToolLifecyclesRef.current.get(discardedInputItemId) ?? {
              activities: [],
              toolCalls: [],
              assistantFiles: [],
            }
            discardedToolLifecyclesRef.current.set(
              discardedInputItemId,
              mergeVoiceToolLifecycles(stashed, response)
            )
          }
          pendingAssistantResponsesRef.current = retainedPending
        }
        accumulatedAssistantTextRef.current = ""
        if (event.inputItemId && !activeAssistantInputItemIdRef.current) {
          activeAssistantInputItemIdRef.current = event.inputItemId
        }
        isStreamingRef.current = true
        onStreamingComplete?.()

        const userText = voiceUserTranscriptRef.current
        const hasToolLifecycle = Boolean(
          currentActivitiesRef.current.length ||
          completedToolCallsRef.current.length ||
          currentAssistantFilesRef.current.length
        )
        if (userText && hasToolLifecycle && onStreamingVoiceMessage) {
          onStreamingVoiceMessage(
            userText,
            createStreamingVoiceResponse({
              text: "",
              activities: currentActivitiesRef.current,
              assistantFiles: currentAssistantFilesRef.current,
              sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
            })
          )
        }
        logger.info("Preserved voice tool lifecycle across provider response handoff", {
          responseId: event.responseId,
        })
        return
      }

      if (event?.reason === "session_failed") {
        // Unlike a manual hang-up, a fatal provider/transport boundary must not
        // flush a partial answer (or an unverified tool/file claim) as complete.
        voiceUserTranscriptRef.current = ""
        voiceUserItemIdRef.current = undefined
        accumulatedAssistantTextRef.current = ""
        activeAssistantInputItemIdRef.current = undefined
        currentActivitiesRef.current = []
        completedToolCallsRef.current = []
        currentAssistantFilesRef.current = []
        isStreamingRef.current = false
        onStreamingComplete?.()
        logger.warn("Discarded active voice turn after realtime session failure", {
          source: event.source,
        })
        return
      }

      if (event?.reason === "response_discarded") {
        const failedInputItemId = event.inputItemId
        const pendingResponses = pendingAssistantResponsesRef.current

        if (failedInputItemId) {
          pendingAssistantResponsesRef.current = pendingResponses.filter(
            (response) => response.inputItemId !== failedInputItemId
          )
          failedTranscriptionItemIdsRef.current.delete(failedInputItemId)
          discardedToolLifecyclesRef.current.delete(failedInputItemId)
        } else {
          // Older proxy events may omit input_item_id. The terminal event still
          // belongs to the currently active response, so discard only the newest
          // anonymous completion instead of erasing unrelated ordered snapshots.
          const anonymousPendingIndex = pendingResponses.findLastIndex(
            (response) => !response.inputItemId
          )
          if (anonymousPendingIndex >= 0) {
            pendingResponses.splice(anonymousPendingIndex, 1)
          }
          if (anonymousFailedTranscriptionsRef.current > 0) {
            anonymousFailedTranscriptionsRef.current -= 1
          }
        }

        const activeInputItemId = activeAssistantInputItemIdRef.current
        const ownsActiveResponse =
          !failedInputItemId || !activeInputItemId || failedInputItemId === activeInputItemId
        if (ownsActiveResponse) {
          // Provider cancellation/failure is not a user barge-in. Nothing from
          // this response may be persisted at hang-up or inherited by a successor.
          voiceUserTranscriptRef.current = ""
          voiceUserItemIdRef.current = undefined
          accumulatedAssistantTextRef.current = ""
          activeAssistantInputItemIdRef.current = undefined
          currentActivitiesRef.current = []
          completedToolCallsRef.current = []
          currentAssistantFilesRef.current = []
          isStreamingRef.current = false
          onStreamingComplete?.()
        }

        logger.warn("Discarded unsuccessful realtime response from voice persistence", {
          responseId: event.responseId,
          status: event.status,
        })
        return
      }

      const pending = pendingAssistantResponsesRef.current.at(0)
      const pendingUserText = voiceUserTranscriptRef.current
      const pendingAssistantText = accumulatedAssistantTextRef.current
      const activeInputItemId = activeAssistantInputItemIdRef.current

      if (consumeFailedTranscription(activeInputItemId)) {
        onStreamingComplete?.()
        accumulatedAssistantTextRef.current = ""
        activeAssistantInputItemIdRef.current = undefined
        currentActivitiesRef.current = []
        completedToolCallsRef.current = []
        currentAssistantFilesRef.current = []
        isStreamingRef.current = false
        return
      }

      if (pending) {
        // response.done can beat the asynchronous user transcription. Barge-in
        // may stop the scheduled audio tail during that gap, but it must not
        // eagerly persist an empty-user exchange or discard the pairing state.
        if (!pendingUserText) return
        pendingAssistantResponsesRef.current.shift()
        commitVoiceExchange({
          userText: pendingUserText,
          assistantText: pending.text,
          activities: pending.activities,
          toolCalls: pending.toolCalls,
          assistantFiles: pending.assistantFiles,
        })
        return
      }

      if (pendingUserText) {
        commitVoiceExchange({
          userText: pendingUserText,
          assistantText: pendingAssistantText,
          activities: currentActivitiesRef.current,
          toolCalls: completedToolCallsRef.current,
          assistantFiles: currentAssistantFilesRef.current,
        })
        return
      }

      // Barge-in before Whisper delivers the user transcript: keep any
      // in-flight assistant text in the pending FIFO. isActiveResponse now
      // drops the interrupted response's assistant_transcript_done, so this
      // snapshot is the only chance to retain the answer for late pairing.
      const hasPendingSnapshot = Boolean(
        pendingAssistantText ||
        currentActivitiesRef.current.length ||
        completedToolCallsRef.current.length ||
        currentAssistantFilesRef.current.length
      )
      if (hasPendingSnapshot) {
        pendingAssistantResponsesRef.current.push({
          text: pendingAssistantText,
          activities: [...currentActivitiesRef.current],
          toolCalls: [...completedToolCallsRef.current],
          assistantFiles: [...currentAssistantFilesRef.current],
          ...(activeInputItemId ? { inputItemId: activeInputItemId } : {}),
        })
      }

      onStreamingComplete?.()
      accumulatedAssistantTextRef.current = ""
      activeAssistantInputItemIdRef.current = undefined
      currentActivitiesRef.current = []
      completedToolCallsRef.current = []
      currentAssistantFilesRef.current = []
      isStreamingRef.current = false
    },
    [
      commitVoiceExchange,
      consumeFailedTranscription,
      currentSessionId,
      onStreamingComplete,
      onStreamingVoiceMessage,
    ]
  )

  /**
   * Handle tool execution starting.
   * Adds an activity message to the streaming response.
   */
  const handleVoiceToolStart = useCallback(
    (toolName: string) => {
      // Add the tool activity
      const activity = createToolActivity(toolName)
      currentActivitiesRef.current = [...currentActivitiesRef.current, activity]

      const userText = voiceUserTranscriptRef.current
      if (!userText || !onStreamingVoiceMessage) return

      // Update the streaming message with activities
      onStreamingVoiceMessage(
        userText,
        createStreamingVoiceResponse({
          text: accumulatedAssistantTextRef.current,
          activities: currentActivitiesRef.current,
          assistantFiles: currentAssistantFilesRef.current,
          sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
        })
      )
    },
    [currentSessionId, onStreamingVoiceMessage]
  )

  /**
   * Handle tool execution completing.
   * Tracks the completed tool call for inclusion in the final message.
   */
  const handleVoiceToolComplete = useCallback(
    (completedTool: RealtimeCompletedTool) => {
      const { name: toolName, success, assistantFiles, callId } = completedTool
      const toolCall: CompletedVoiceTool = {
        name: toolName,
        ...(callId ? { callId } : {}),
        success,
        ...(assistantFiles.length ? { assistantFiles } : {}),
      }
      completedToolCallsRef.current = mergeCompletedVoiceTools(completedToolCallsRef.current, [
        toolCall,
      ])
      currentAssistantFilesRef.current = Array.from(
        new Set([...currentAssistantFilesRef.current, ...assistantFiles])
      )
      currentActivitiesRef.current = [
        ...currentActivitiesRef.current,
        {
          type: "activity",
          activity_type: toolName,
          message: `${toolName} ${success ? "completed" : "failed"}`,
          details: {
            success,
            ...(assistantFiles.length ? { assistant_files: assistantFiles } : {}),
          },
          timestamp: Date.now(),
        },
      ]

      const userText = voiceUserTranscriptRef.current
      if (!userText || !onStreamingVoiceMessage) return
      onStreamingVoiceMessage(
        userText,
        createStreamingVoiceResponse({
          text: accumulatedAssistantTextRef.current,
          activities: currentActivitiesRef.current,
          assistantFiles: currentAssistantFilesRef.current,
          sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
        })
      )
    },
    [currentSessionId, onStreamingVoiceMessage]
  )

  /**
   * Handle streaming text deltas from the assistant.
   * Creates/updates a streaming message in the chat UI.
   */
  const handleVoiceAssistantTranscriptDelta = useCallback(
    (delta: string, inputItemId?: string) => {
      const boundItemId = voiceUserItemIdRef.current
      if (isMismatchedVoiceItem(inputItemId, boundItemId)) {
        // Bound successor turns must not inherit leftover assistant output for a
        // different input item. Missing ids still attach (late Whisper).
        const pending = pendingAssistantResponsesRef.current.find(
          (response) => response.inputItemId === inputItemId
        )
        if (pending) {
          pending.text += delta
        }
        return
      }

      // Accumulate regardless of whether the user transcript has arrived yet —
      // for short utterances the Whisper transcription can lag the assistant, and
      // dropping these deltas would lose the answer entirely.
      accumulatedAssistantTextRef.current += delta
      if (inputItemId) {
        activeAssistantInputItemIdRef.current = inputItemId
      }
      isStreamingRef.current = true

      const userText = voiceUserTranscriptRef.current
      if (!userText || !onStreamingVoiceMessage) return

      // Update the streaming message with current activities
      onStreamingVoiceMessage(
        userText,
        createStreamingVoiceResponse({
          text: accumulatedAssistantTextRef.current,
          activities: currentActivitiesRef.current,
          assistantFiles: currentAssistantFilesRef.current,
          sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
        })
      )
    },
    [currentSessionId, onStreamingVoiceMessage]
  )

  /**
   * Handle complete assistant transcript.
   * Finalizes the streaming message with activities and tool calls.
   */
  const handleVoiceAssistantTranscript = useCallback(
    (text: string, completion?: RealtimeAssistantCompletion) => {
      const inputItemId = completion?.inputItemId ?? activeAssistantInputItemIdRef.current
      const userItemId = voiceUserItemIdRef.current
      if (isMismatchedVoiceItem(inputItemId, userItemId)) {
        // Pair with a pending snapshot for that item; drop if already committed.
        const pending = pendingAssistantResponsesRef.current.find(
          (response) => response.inputItemId === inputItemId
        )
        if (pending) {
          mergeRealtimeCompletionIntoSnapshot(pending, text, completion)
        }
        return
      }

      if (consumeFailedTranscription(inputItemId)) {
        logger.info("Skipped persistence for voice turn with unavailable input transcript")
        onStreamingComplete?.()
        accumulatedAssistantTextRef.current = ""
        activeAssistantInputItemIdRef.current = undefined
        currentActivitiesRef.current = []
        completedToolCallsRef.current = []
        currentAssistantFilesRef.current = []
        isStreamingRef.current = false
        return
      }

      if (completion) {
        completedToolCallsRef.current = mergeCompletedVoiceTools(
          completedToolCallsRef.current,
          completedToolsFromRealtime(completion.toolCalls)
        )
        currentActivitiesRef.current = mergeVoiceActivities(
          currentActivitiesRef.current,
          completion
        )
        currentAssistantFilesRef.current = Array.from(
          new Set([
            ...currentAssistantFilesRef.current,
            ...completion.assistantFiles,
            ...assistantFilesFromToolCalls(completedToolCallsRef.current),
          ])
        )
      }

      const userText = voiceUserTranscriptRef.current
      if (!text) {
        if (userText && onStreamingVoiceMessage && currentAssistantFilesRef.current.length) {
          onStreamingVoiceMessage(
            userText,
            createStreamingVoiceResponse({
              text: accumulatedAssistantTextRef.current,
              activities: currentActivitiesRef.current,
              assistantFiles: currentAssistantFilesRef.current,
              sessionId: voiceSessionIdRef.current ?? currentSessionId ?? undefined,
            })
          )
        }
        return
      }
      const canPairWithActiveUser = Boolean(
        userText && (!inputItemId || !userItemId || inputItemId === userItemId)
      )
      if (canPairWithActiveUser) {
        // Normal path: the user transcript is already present — commit now.
        commitVoiceExchange({
          userText,
          assistantText: text,
          activities: currentActivitiesRef.current,
          toolCalls: completedToolCallsRef.current,
          assistantFiles: currentAssistantFilesRef.current,
        })
        return
      }

      // The user transcript hasn't arrived yet (short-utterance Whisper lag).
      // Buffer the completed answer; handleVoiceUserTranscript commits it once the
      // late transcript lands, and handleVoiceModeEnd flushes it if voice mode
      // closes first. Either way the answer is never silently discarded.
      // The real answer for an item whose native output was discarded inherits
      // the tools that already ran, so its late transcript pairs with one entry.
      const discardedLifecycle = takeDiscardedToolLifecycle(inputItemId)
      const lifecycle = mergeVoiceToolLifecycles(
        discardedLifecycle ?? { activities: [], toolCalls: [], assistantFiles: [] },
        {
          activities: currentActivitiesRef.current,
          toolCalls: completedToolCallsRef.current,
          assistantFiles: currentAssistantFilesRef.current,
        }
      )
      pendingAssistantResponsesRef.current.push({
        text,
        ...lifecycle,
        ...(inputItemId ? { inputItemId } : {}),
        ...(completion?.unprompted ? { unprompted: true } : {}),
      })
      // Snapshot isolation: a successor can begin before the ordered late user
      // transcript arrives. Its deltas and activities need fresh active refs.
      accumulatedAssistantTextRef.current = ""
      activeAssistantInputItemIdRef.current = undefined
      currentActivitiesRef.current = []
      completedToolCallsRef.current = []
      currentAssistantFilesRef.current = []
      isStreamingRef.current = false
      // Clear the streaming bubble; the exchange lands in history on commit.
      onStreamingComplete?.()
    },
    [
      commitVoiceExchange,
      consumeFailedTranscription,
      currentSessionId,
      onStreamingComplete,
      onStreamingVoiceMessage,
      takeDiscardedToolLifecycle,
    ]
  )

  const handleVoiceSessionCreated = useCallback((newSessionId: string) => {
    logger.info("Voice mode created new session", { sessionId: newSessionId })
    voiceSessionIdRef.current = newSessionId
  }, [])

  const handleVoiceModeEnd = useCallback(() => {
    const sessionToNavigate = voiceSessionIdRef.current
    const activeSessionId = sessionToNavigate ?? currentSessionId ?? undefined
    const activeUserText = voiceUserTranscriptRef.current
    const activeAssistantText = accumulatedAssistantTextRef.current
    const activeActivities = [...currentActivitiesRef.current]
    const activeToolCalls = [...completedToolCallsRef.current]
    const activeAssistantFiles = [...currentAssistantFilesRef.current]
    const canPersist = Boolean(onVoiceMessageRef.current)

    // Flush a completed answer whose (late) user transcript never arrived before
    // voice mode closed, so the exchange isn't lost. Empty userText is acceptable
    // here — the answer is the valuable part.
    const pendingResponses = pendingAssistantResponsesRef.current
    if (pendingResponses.length > 0 && canPersist) {
      for (const pendingResponse of pendingResponses) {
        persistVoiceMessage(
          "",
          createVoiceTranscriptResponse(pendingResponse.text, {
            activities: pendingResponse.activities,
            toolCalls: pendingResponse.toolCalls,
            assistantFiles: pendingResponse.assistantFiles,
            sessionId: activeSessionId,
          })
        )
      }
      logger.info("Flushed pending assistant answer on voice mode end", {
        pendingResponseCount: pendingResponses.length,
      })
    }
    pendingAssistantResponsesRef.current = []

    // Tools that ran for an item whose native output was discarded, but whose
    // user turn and real answer never arrived: keep them, without any
    // discarded model text, rather than losing the verified tool result.
    const discardedLifecycles = [...discardedToolLifecyclesRef.current.values()]
    if (discardedLifecycles.length > 0 && canPersist) {
      for (const lifecycle of discardedLifecycles) {
        persistVoiceMessage(
          "",
          createVoiceTranscriptResponse("", { ...lifecycle, sessionId: activeSessionId })
        )
      }
    }
    discardedToolLifecyclesRef.current.clear()

    // Snapshot before flushing completed pending turns: async persistence and
    // pending cleanup must not erase a partially streamed successor.
    const hasActiveExchange = Boolean(
      activeUserText ||
      activeAssistantText ||
      activeActivities.length ||
      activeToolCalls.length ||
      activeAssistantFiles.length
    )
    if (hasActiveExchange && canPersist) {
      const response = createVoiceTranscriptResponse(activeAssistantText, {
        activities: activeActivities,
        toolCalls: activeToolCalls,
        assistantFiles: activeAssistantFiles,
        sessionId: activeSessionId,
      })
      persistVoiceMessage(activeUserText, response)
      logger.info("Saved in-progress exchange on voice mode end", {
        userTextLength: activeUserText.length,
        assistantTextLength: activeAssistantText.length,
      })
    }
    voiceUserTranscriptRef.current = ""
    voiceUserItemIdRef.current = undefined
    accumulatedAssistantTextRef.current = ""
    activeAssistantInputItemIdRef.current = undefined
    currentActivitiesRef.current = []
    completedToolCallsRef.current = []
    currentAssistantFilesRef.current = []
    isStreamingRef.current = false
    failedTranscriptionItemIdsRef.current.clear()
    anonymousFailedTranscriptionsRef.current = 0

    // Clear streaming UI state
    onStreamingComplete?.()

    // Always invalidate sessions list so new voice sessions appear in sidebar
    void queryClient.invalidateQueries({
      queryKey: ["session-history"],
    })

    // If voice mode was used in an existing session, invalidate the messages cache
    if (currentSessionId) {
      logger.info("Voice mode ended, invalidating messages cache", { sessionId: currentSessionId })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionMessages(currentSessionId),
      })
    }

    // If a session was created during voice mode, navigate to it now
    // Read after the hang-up flushes above, which also count as committed.
    const committedInRun = committedInCurrentRunRef.current
    committedInCurrentRunRef.current = false
    if (sessionToNavigate && !currentSessionId && !committedInRun) {
      logger.info("Voice mode ended without content, not navigating to its session", {
        sessionId: sessionToNavigate,
      })
    }
    if (sessionToNavigate && !currentSessionId && committedInRun) {
      logger.info("Voice mode ended, navigating to session", { sessionId: sessionToNavigate })

      // Delay navigation to allow backend to save conversation first
      setTimeout(() => {
        void navigate(`${voiceSessionRoutePrefix}/${sessionToNavigate}`, { replace: true })
      }, 500)
    }
    voiceSessionIdRef.current = null
  }, [
    currentSessionId,
    navigate,
    queryClient,
    onStreamingComplete,
    persistVoiceMessage,
    voiceSessionRoutePrefix,
  ])

  /**
   * A fair-use refusal frame from the realtime socket (`fair_use_blocked` /
   * `plan_upgrade_required`). Routed through the same bridge the chat WS uses,
   * so the budget indicator refreshes and the popover / plan-upgrade notice
   * opens instead of the user being shown a generic "service error".
   *
   * It lives here, not in `use-realtime-audio`, because routing needs the
   * QueryClient and the composer has a tested contract that it renders without
   * a `QueryClientProvider` — this hook is the nearest consumer that already
   * holds one.
   */
  const handleVoiceFairUseRefusal = useCallback(
    (frame: unknown) => {
      routeFairUseError(queryClient, frame)
    },
    [queryClient]
  )

  return {
    handleVoiceFairUseRefusal,
    handleVoiceUserTranscript,
    handleVoiceTranscriptionFailure,
    handleVoiceAssistantTranscriptDelta,
    handleVoiceAssistantTranscript,
    handleVoiceToolStart,
    handleVoiceToolComplete,
    handleVoiceInterrupt,
    handleVoiceSessionCreated,
    handleVoiceModeEnd,
  }
}
